// Pure readers + matchers over the AHP host's dynamic project-management surface:
// the `copilot.projectManagement` capability bit and the `copilot.projects`
// catalogue on `RootState.config`, shared by the relay runtime and its tests.
// Spec: github/copilot-host spec/meta-conventions/copilot/root-state.md

import type {RootState} from '@microsoft/agent-host-protocol'

/** Lifecycle of a project entry; only `ready` git projects are launchable. */
export type ProjectStatus = 'ready' | 'cloning' | 'failed'

export type ProjectOrigin = 'pinned' | 'cloned'

/** A single entry in the host's `copilot.projects` catalogue. */
export interface ProjectEntry {
  /** Opaque, restart-stable id. */
  id: string
  name: string
  /** Canonical absolute host path; the `createSession` working dir. */
  path: string
  origin: ProjectOrigin
  /** Git projects are worktree-capable. */
  git: boolean
  status: ProjectStatus
  /** Clone percent (0..100); present only while `cloning`. */
  progress?: number
  /** Present only while `failed`. */
  error?: string
  /** Sanitized `origin` remote URL, credentials stripped. */
  remoteUrl?: string
  /** Populated once a clone settles to `ready`. */
  defaultBranch?: string
  bootPinned?: boolean
}

interface CopilotConfigNamespace {
  hostName?: string
  projects?: ProjectEntry[]
}

const META_PROJECT_MANAGEMENT_KEY = 'copilot.projectManagement'
const CONFIG_COPILOT_KEY = 'copilot'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

/** Whether the host advertises dynamic project management; when false the `extensions/*Project` commands must not be called. */
export function readProjectManagementAvailable(rootState: RootState | undefined): boolean {
  const meta = rootState?._meta
  if (!isRecord(meta)) return false
  const cap = meta[META_PROJECT_MANAGEMENT_KEY]
  return isRecord(cap) && cap.available === true
}

/** Narrow an unknown array element to a minimally-valid `ProjectEntry`. */
function isProjectEntry(value: unknown): value is ProjectEntry {
  return (
    isRecord(value) &&
    typeof value.id === 'string' &&
    typeof value.path === 'string' &&
    typeof value.status === 'string'
  )
}

function readProjectsFromNamespace(namespace: unknown): ProjectEntry[] {
  if (!isRecord(namespace)) return []
  const projects = (namespace as CopilotConfigNamespace).projects
  if (!Array.isArray(projects)) return []
  return projects.filter(isProjectEntry)
}

/** Read the current projects catalogue from a `RootState` snapshot; empty when none is published. */
export function readProjectsCatalogue(rootState: RootState | undefined): ProjectEntry[] {
  return readProjectsFromNamespace(rootState?.config?.values?.[CONFIG_COPILOT_KEY])
}

/**
 * Read the projects catalogue out of a `root/configChanged` delta. `undefined`
 * when the delta doesn't touch the `copilot` namespace, so callers can
 * distinguish "no change" from "now empty".
 */
export function readProjectsFromConfigDelta(config: Record<string, unknown> | undefined): ProjectEntry[] | undefined {
  if (!isRecord(config) || !(CONFIG_COPILOT_KEY in config)) return undefined
  return readProjectsFromNamespace(config[CONFIG_COPILOT_KEY])
}

/** A repository identity: its `owner/repo` key plus host authority when the source carried one. */
export interface RepoLocator {
  /** `owner/repo`, lower-cased. */
  key: string
  /** Lower-cased host authority (no port), when present. */
  host?: string
}

/**
 * Parse a git remote / clone URL / bare `owner/repo` into a {@link RepoLocator}.
 * `undefined` when no `owner/repo` can be extracted (e.g. a local path).
 */
export function canonicalRepoLocator(value: string | undefined): RepoLocator | undefined {
  if (!value) return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined

  let path: string
  let host: string | undefined
  // scp-like syntax: git@github.com:owner/repo.git
  const scpMatch = trimmed.match(/^[^/@]+@([^:/]+):(.+)$/)
  if (scpMatch) {
    host = scpMatch[1]?.toLowerCase()
    path = scpMatch[2] ?? ''
  } else {
    const schemeMatch = trimmed.match(/^[a-z][a-z0-9+.-]*:\/\/([^/]+)(\/.*)?$/i)
    if (schemeMatch) {
      const authority = schemeMatch[1] ?? ''
      host = (authority.split('@').pop() ?? '').split(':')[0]?.toLowerCase() || undefined
      path = schemeMatch[2] ?? ''
    } else if (trimmed.startsWith('/')) {
      return undefined
    } else {
      path = trimmed
    }
  }

  // `split('/').filter(Boolean)` discards empty segments without a slash-stripping
  // regex (which would be super-linear on inputs with many '/').
  const segments = path.split('/').filter(Boolean)
  const repoSegment = segments.at(-1)
  const ownerSegment = segments.at(-2)
  if (!ownerSegment || !repoSegment) return undefined
  const repo = repoSegment.replace(/\.git$/i, '')
  return {key: `${ownerSegment}/${repo}`.toLowerCase(), ...(host ? {host} : {})}
}

/** Normalize a git remote or repo nwo to a canonical `owner/repo` key (host discarded). */
export function canonicalRepoKey(value: string | undefined): string | undefined {
  return canonicalRepoLocator(value)?.key
}

/**
 * Find the catalogue entry for a clone URL, matched host-aware so a same-nwo
 * collision across hosts (e.g. dotcom vs GHES `acme/app`) can't launch against
 * the wrong checkout. Falls back to `owner/repo`-only when the entry's remote
 * host is unknown.
 */
export function matchProjectByCloneUrl(projects: ProjectEntry[], cloneUrl: string): ProjectEntry | undefined {
  const want = canonicalRepoLocator(cloneUrl)
  if (!want) return undefined
  return projects.find(entry => {
    const got = canonicalRepoLocator(entry.remoteUrl)
    if (!got || got.key !== want.key) return false
    if (got.host && want.host) return got.host === want.host
    return true
  })
}

/** Find the catalogue entry for a repository nwo, matching `owner/repo` without host disambiguation. */
export function matchProjectByNwo(projects: ProjectEntry[], nwo: string): ProjectEntry | undefined {
  const target = canonicalRepoKey(nwo)
  if (!target) return undefined
  return projects.find(entry => canonicalRepoKey(entry.remoteUrl) === target)
}
